import { isParentUser } from 'src/utils/userRole';
import { isNative } from 'src/utils/platform';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState, FunctionComponent } from 'react';
import { useCurrentUser } from 'src/queries/user';
import {
  CustomerInfo,
  PurchasesEntitlementInfo,
} from '@revenuecat/purchases-capacitor';
import {
  getActiveSubscription,
  logInAppPurchase,
  syncPurchasesCompat,
  getCustomerInfoCompat,
  presentNativePaywall,
  PAYWALL_RESULT,
} from 'src/modules/purchase/RevenueCat';
import { logPaywallConversion } from 'src/modules/purchase/logPaywallConversion';
import dayjs from 'dayjs';
import WebPaywallTrigger from 'src/components/Paywall/WebPaywallTrigger';
import StartTrialPanel from 'src/components/Paywall/StartTrialPanel';
import { User } from 'src/types/user.types';
import { Location, useLocation } from 'react-router-dom';
import { reportError } from 'src/modules/logs/Sentry';
import {
  logAnalyticsEvent,
  trackPaywallError,
} from 'src/modules/analytics/analytics';
import { getErrorCodeName, getErrorMessage } from 'src/modules/purchase/RevenueCat';
import { useFetchSubscriptions } from 'src/queries/subscriptions';
import UserContextProvider from 'src/contexts/UserContext';
import { RevenueCatPlacement } from 'src/modules/purchase/purchase.constants';
import { usePostHogFeatureFlag } from 'src/hooks/usePostHogFeatureFlag';
import { ROUTES } from 'src/config/routes';

type PaywallType = {
  isPremium: boolean;
  nbTrialDays: number;
  isOutOfTrial: boolean;
  isFreemium: boolean;
  userTrialEndsAt?: string;
  familyTrialEndsAt?: string;
  activeSubscription?: PurchasesEntitlementInfo;
  managementURL?: string;
  showPaywall: () => void;
  refreshCustomerInfo: () => Promise<void>;
};

const PaywallContext = createContext<PaywallType | null>(null);

const DEFAULT_NB_TRIAL_DAYS = 7;
const BETA_TESTER_NB_TRIAL_DAYS = 30;

const PATHS_WITHOUT_PAYWALL = [
  ROUTES.TERMS_AND_CONDITIONS.path,
  ROUTES.CONFIDENTIALITE.path,
  ROUTES.ONBOARDING.path,
];
const ignorePaywall = (location: Location) => {
  return PATHS_WITHOUT_PAYWALL.some(path => location.pathname.includes(path));
};

const isOutOfTrialFn = (
  userTrialEndsAt?: string,
  familyTrialEndsAt?: string,
) => {
  const now = dayjs();
  const userExpired = userTrialEndsAt ? dayjs(userTrialEndsAt) < now : true;
  const familyExpired = familyTrialEndsAt
    ? dayjs(familyTrialEndsAt) < now
    : true;
  return (
    (!!userTrialEndsAt || !!familyTrialEndsAt) && userExpired && familyExpired
  );
};
const isPremiumFn = (premium_ends_at?: string) => {
  return premium_ends_at ? dayjs(premium_ends_at) > dayjs() : false;
};

type NativePaywallTriggerProps = {
  user: Pick<User, 'id' | 'family_id'>;
  isOpen: boolean;
  isBlocking: boolean;
  onPurchased: () => Promise<void>;
  onDismiss: () => void;
};

const NativePaywallTrigger: FunctionComponent<NativePaywallTriggerProps> = ({
  user,
  isOpen,
  isBlocking,
  onPurchased,
  onDismiss,
}) => {
  const presentingRef = useRef(false);
  const userRef = useRef(user);
  const onPurchasedRef = useRef(onPurchased);
  const onDismissRef = useRef(onDismiss);
  const isBlockingRef = useRef(isBlocking);
  useEffect(() => {
    userRef.current = user;
    onPurchasedRef.current = onPurchased;
    onDismissRef.current = onDismiss;
    isBlockingRef.current = isBlocking;
  });

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    if (isBlockingRef.current) {
      logAnalyticsEvent('trial_ended');
      logAnalyticsEvent('paywall_viewed', {
        type: 'blocking_trial_ended',
        placement: RevenueCatPlacement.PAYWALL_PANEL,
      });
    }

    const present = async () => {
      if (presentingRef.current || cancelled) return;
      presentingRef.current = true;
      try {
        const result = await presentNativePaywall({
          user: userRef.current,
          placement: RevenueCatPlacement.PAYWALL_PANEL,
          displayCloseButton: true,
          listener: {
            onPurchaseCompleted: ({ customerInfo, storeTransaction }) => {
              logPaywallConversion(
                storeTransaction.productIdentifier,
                RevenueCatPlacement.PAYWALL_PANEL,
                customerInfo,
                userRef.current.family_id,
              );
            },
          },
        });
        presentingRef.current = false;
        if (cancelled) return;
        if (
          result === PAYWALL_RESULT.PURCHASED ||
          result === PAYWALL_RESULT.RESTORED
        ) {
          await onPurchasedRef.current();
        } else {
          onDismissRef.current();
        }
      } catch (error) {
        reportError('Native paywall error', error);
        presentingRef.current = false;
        if (cancelled) return;
        onDismissRef.current();
      }
    };

    present();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  return null;
};

const PaywallProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<{
    customerInfo?: CustomerInfo;
  }>({});
  const [trial, setTrial] = useState<{
    key?: string;
    isOutOfTrial: boolean;
    isPremium: boolean;
  }>({ isOutOfTrial: false, isPremium: false });
  const [isPaywallOpen, setIsPaywallOpen] = useState<boolean>(false);
  const { data: user } = useCurrentUser();
  const isParent = isParentUser(user);
  const { mutateAsync: refetchSubscriptions } = useFetchSubscriptions();
  const { value: freeFirstLessonEnabled } = usePostHogFeatureFlag('is_free_first_lesson_enabled');

  const location = useLocation();

  const initialize = useCallback(
    async (
      user: Pick<User, 'id' | 'family_id'> & {
        premiumEndDate?: string;
      },
    ) => {
      try {
        const premiumEndDate = user.premiumEndDate;
        const customerInfo = await logInAppPurchase(user, 'boot');
        if (customerInfo) {
          const latestExpirationDate = customerInfo?.latestExpirationDate;
          if (premiumEndDate !== latestExpirationDate) {
            await refetchSubscriptions();
          }
        }

        return { customerInfo };
      } catch (error) {
        trackPaywallError({
          operation: 'login',
          error_code: getErrorCodeName(error),
          error_message: getErrorMessage(error),
        });
        reportError('Error login in revenuecat', error);
      }
    },
    [refetchSubscriptions],
  );

  // `user` undefined means not loaded or query cleared by a switch, never logged out: keep
  // the RC identity, drop the display cache.
  if (!(user?.id && user?.family_id) && state.customerInfo !== undefined) {
    setState(state => ({ ...state, customerInfo: undefined }));
  }

  useEffect(() => {
    if (user?.id && user?.family_id) {
      initialize({
        id: user.id,
        family_id: user.family_id,
        premiumEndDate: user.family?.premium_ends_at,
      }).then(result => {
        if (result) {
          setState(state => ({ ...state, customerInfo: result.customerInfo }));
        }
      });
    }
  }, [initialize, user?.id, user?.family_id, user?.family?.premium_ends_at]);

  const refreshCustomerInfo = useCallback(async () => {
    try {
      await syncPurchasesCompat();
      const { customerInfo } = await getCustomerInfoCompat();
      setState(state => ({ ...state, customerInfo }));
    } catch (error) {
      reportError('Failed to refresh RevenueCat customer info', error);
    }
  }, []);

  const trialKey = [
    user?.trial_ends_at,
    user?.family?.trial_ends_at,
    user?.family?.premium_ends_at,
    user?.onboarding_completed,
    user?.created_at,
    isParent,
  ].join('|');
  if (trial.key !== trialKey) {
    const isPremium = isPremiumFn(user?.family?.premium_ends_at);
    const isOutOfTrial =
      !isPremium &&
      isOutOfTrialFn(user?.trial_ends_at, user?.family?.trial_ends_at);
    setTrial({ key: trialKey, isOutOfTrial, isPremium });
    if (isOutOfTrial && user?.onboarding_completed && isParent) {
      setIsPaywallOpen(true);
    }
  }

  const familyHasLesson = (user?.lessons_count ?? 0) >= 1;
  const isFreemium =
    trial.isOutOfTrial ||
    (freeFirstLessonEnabled &&
      !isPremiumFn(user?.family?.premium_ends_at) &&
      familyHasLesson);

  const showPaywall = useCallback(() => {
    setIsPaywallOpen(true);
  }, []);

  const nbTrialDaysValue =
    user?.created_at && dayjs(user.created_at).isBefore('2024-09-01')
      ? BETA_TESTER_NB_TRIAL_DAYS
      : DEFAULT_NB_TRIAL_DAYS;
  const userTrialEndsAt = user?.trial_ends_at;
  const familyTrialEndsAt = user?.family?.trial_ends_at;
  const activeSubscription = getActiveSubscription(state?.customerInfo);
  const managementURL = state?.customerInfo?.managementURL ?? undefined;

  const value = useMemo(
    () => ({
      isPremium: trial.isPremium,
      nbTrialDays: nbTrialDaysValue,
      isOutOfTrial: trial.isOutOfTrial,
      isFreemium,
      userTrialEndsAt,
      familyTrialEndsAt,
      activeSubscription,
      managementURL,
      showPaywall,
      refreshCustomerInfo,
    }),
    [
      trial.isPremium,
      trial.isOutOfTrial,
      nbTrialDaysValue,
      isFreemium,
      userTrialEndsAt,
      familyTrialEndsAt,
      activeSubscription,
      managementURL,
      showPaywall,
      refreshCustomerInfo,
    ],
  );

  return (
    <PaywallContext.Provider value={value}>
      {children}
      {user ? (
        <UserContextProvider me={user}>
          <StartTrialPanel />
          {isNative() ? (
            <NativePaywallTrigger
              user={user}
              isOpen={isPaywallOpen && !ignorePaywall(location)}
              isBlocking={trial.isOutOfTrial}
              onPurchased={async () => {
                await refetchSubscriptions();
                setIsPaywallOpen(false);
              }}
              onDismiss={() => setIsPaywallOpen(false)}
            />
          ) : (
            <WebPaywallTrigger
              isOpen={isPaywallOpen && !ignorePaywall(location)}
              host="overlay"
              placement={RevenueCatPlacement.PAYWALL_PANEL}
              // Full-app block: a presentation failure here must not dismiss, or an out-of-trial
              // parent gets premium for free.
              isBlocking
              shouldLogPaywallEvents
              onPurchased={async () => {
                // Close first: a refetch failure must not strand the transparent overlay open and
                // swallow every tap.
                setIsPaywallOpen(false);
                try {
                  await refetchSubscriptions();
                } catch (error) {
                  reportError('Failed to refetch subscriptions after web purchase', error);
                }
              }}
              onDismiss={() => setIsPaywallOpen(false)}
            />
          )}
        </UserContextProvider>
      ) : null}
    </PaywallContext.Provider>
  );
};

export const usePaywall = () => {
  const paywallContext = useContext(PaywallContext);
  if (!paywallContext) {
    throw new Error('usePaywall must be used within a PaywallContext');
  }
  return paywallContext;
};

export const usePaywallMaybe = () => useContext(PaywallContext);

export default PaywallProvider;
